"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { Archive, MoreHorizontal } from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/animate-ui/primitives/radix/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useNotebook } from "@/lib/notebook/core/store";
import StaticIcon, { STATIC_ICONS } from "@/components/ui/static-icon";

export default function NotebookOptionsMenu({
  triggerClassName = "nb-options-trigger group",
  triggerIcon: TriggerIcon = MoreHorizontal,
  disabled = false,
}) {
  const router = useRouter();
  const notebookId = useNotebook((s) => s.notebookId);
  const notebookTitle = useNotebook((s) => s.title);
  const deleteNotebook = useNotebook((s) => s.deleteNotebook);
  const locked = useNotebook((s) => s.locked);
  const planReadOnly = useNotebook((s) => s.planReadOnly);
  const promptPlanReadOnly = useNotebook((s) => s.promptPlanReadOnly);
  const readOnly = disabled || locked;

  const isPinned = useNotebook((s) => s.isPinned);
  const isArchived = useNotebook((s) => s.isArchived);
  const setPinned = useNotebook((s) => s.setPinned);
  const setArchived = useNotebook((s) => s.setArchived);

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);

  if (!notebookId) return null;

  const handleDelete = async () => {
    setDeleteBusy(true);
    try {
      await deleteNotebook();
      toast.success("Notebook deleted");
      router.push("/home");
    } catch {
      toast.error("Could not delete notebook");
    } finally {
      setDeleteBusy(false);
      setConfirmDelete(false);
    }
  };

  const handleTriggerClick = (event) => {
    if (readOnly && planReadOnly) {
      event.preventDefault();
      promptPlanReadOnly("note");
    }
  };

  return (
    <>
      <DropdownMenu modal={!readOnly}>
        <DropdownMenuTrigger asChild disabled={readOnly && !planReadOnly}>
          <button
            type="button"
            className={`${triggerClassName}${readOnly ? " is-disabled" : ""}`}
            aria-label="Notebook options"
            disabled={readOnly && !planReadOnly}
            onClick={handleTriggerClick}
            title={
              readOnly ? "Preserved on your current plan" : "Notebook options"
            }
          >
            <TriggerIcon
              className="size-[15px] opacity-80 transition-opacity group-hover:opacity-100"
              strokeWidth={2}
            />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" sideOffset={8} className="nb-options-menu">
          <DropdownMenuItem
            onClick={() => void setArchived(!isArchived)}
            className="nb-options-menu__item"
          >
            <Archive size={14} strokeWidth={1.75} className="nb-options-menu__icon" />
            <span className="nb-options-menu__label">
              {isArchived ? "Unarchive notebook" : "Archive notebook"}
            </span>
          </DropdownMenuItem>

          <DropdownMenuItem
            onClick={() => void setPinned(!isPinned)}
            className="nb-options-menu__item"
          >
            <StaticIcon
              src={STATIC_ICONS.pin}
              size={14}
              variant="adaptive"
              className="nb-options-menu__icon"
            />
            <span className="nb-options-menu__label">
              {isPinned ? "Unpin from home" : "Pin to home"}
            </span>
          </DropdownMenuItem>

          <DropdownMenuSeparator className="nb-options-menu__separator" />

          <DropdownMenuItem
            onClick={() => setConfirmDelete(true)}
            className="nb-options-menu__item nb-options-menu__item--danger"
          >
            <StaticIcon
              src={STATIC_ICONS.trash}
              size={14}
              variant="adaptive"
              className="nb-options-menu__icon"
            />
            <span className="nb-options-menu__label">Delete notebook</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent className="nb-options-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this notebook?</AlertDialogTitle>
            <AlertDialogDescription>
              {notebookTitle?.trim()
                ? `"${notebookTitle.trim()}" will move to Trash. You can restore it later.`
                : "This notebook will move to Trash. You can restore it later."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteBusy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteBusy}
              onClick={(event) => {
                event.preventDefault();
                void handleDelete();
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteBusy ? "Deleting…" : "Delete notebook"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
